/**
 * Инициализация i18next — центральная конфигурация интернационализации
 *
 * Переводы хранятся как JSON файлы в public/locales/{lang}/{namespace}.json
 * и загружаются через i18next-http-backend.
 *
 * Архитектура:
 * - 13 namespace, разделённых по логическим областям продукта
 *   (не по FSD слоям — это усложняло поддержку):
 *     common          — общие вещи (кнопки, ошибки, валидация, валюты, иконки, даты)
 *     navigation      — навигация, BottomMenu
 *     auth            — вход/регистрация/восстановление пароля
 *     home            — главная: лента, подборки, популярное, публичные вишлисты
 *     wishlist        — вишлисты: список, детальная, форма, соавторы
 *     wishlistItem    — желания: детальная, форма, парсер товаров
 *     profile         — профиль: свой/чужой, редактирование, блокировка, заблокированные
 *     friends         — друзья, запросы, community page ("Мои друзья")
 *     secretSanta     — Тайный Санта (карточка, список, детальная, форма, пары)
 *     notifications   — список уведомлений (НЕ настройки push — те в settings)
 *     settings        — все настройки + приложения push
 *     feed            — фид активности
 *     bookings        — мои бронирования
 *
 * - При старте приложения загружаются ВСЕ namespace для текущего языка
 *   единой пачкой (13 параллельных HTTP запросов)
 * - Lazy loading намеренно отключён — проект на финальной стадии,
 *   объём переводов небольшой (~70KB на язык до gzip),
 *   скачки текста при переходах страниц недопустимы в мобильном приложении
 * - При смене языка в настройках — догружается новый язык, lazy
 *
 * Использование в компонентах:
 *   import { useTranslation } from 'react-i18next';
 *
 *   const { t } = useTranslation();
 *   t('common:save');                          // → "Сохранить"
 *   t('wishlist:wishlist.card.noImage');               // → "Нет фото"
 *   t('profile:editProfile.title');           // → "Редактировать профиль"
 *
 * Двоеточие — разделитель namespace, точка — разделитель вложенности внутри JSON.
 */

import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

/** Список всех namespace которые используются в приложении */
const NAMESPACES = [
  'common',
  'navigation',
  'auth',
  'home',
  'wishlist',
  'wishlistItem',
  'profile',
  'friends',
  'secretSanta',
  'notifications',
  'settings',
  'feed',
  'bookings',
] as const;

/** Инициализируем i18next один раз при загрузке модуля */
void i18n
  .use(HttpBackend)
  .use(initReactI18next)
  .init({
    // Загрузка JSON файлов через HTTP из public/locales/
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
    },

    // Язык по умолчанию и фолбэк
    lng: 'ru',
    fallbackLng: 'ru',

    // Все namespace грузим при старте — см. комментарий в начале файла
    ns: NAMESPACES as unknown as string[],
    defaultNS: 'common',

    // Грузим только текущий язык, не все сразу (на случай когда добавим языки)
    load: 'currentOnly',

    // Интерполяция переменных {{var}}
    interpolation: {
      // React сам экранирует HTML, поэтому экранирование i18next не нужно
      escapeValue: false,
    },

    // React Suspense — компоненты автоматически ждут загрузку переводов
    react: {
      useSuspense: true,
    },

    // В dev режиме логируем что происходит (в проде отключим)
    debug: import.meta.env.DEV,
  });

export default i18n;
